import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { useBrandAndContact } from "@/hooks/useBrandAndContact";
import { useMarketingImage } from "@/hooks/useMarketingImage";
import { SERVICES } from "@/data/services";
import { useSEO } from "@/hooks/useSEO";
import { useHomeFeatured } from "@/hooks/useHomeFeatured";
import { useServiceCovers } from "@/hooks/useServiceCovers";
import ServiceCard from "@/components/ServiceCard";
import SectionTitle from "@/components/SectionTitle";

const HERO_IMAGE_DEFAULT =
  "https://images.pexels.com/photos/35247374/pexels-photo-35247374.jpeg";

const STUDIO_IMAGE_DEFAULT =
  "https://images.pexels.com/photos/17345669/pexels-photo-17345669.jpeg";

const Home = () => {
  const { t, lang } = useLanguage();
  const { isVisible, c } = useSiteContent();
  const CONTACT = useBrandAndContact();
  const { items: featured } = useHomeFeatured();
  const { covers: serviceCovers } = useServiceCovers();
  // Editorial photos — admin can override via /admin → Images marketing.
  const HERO_IMAGE = useMarketingImage("home.hero", HERO_IMAGE_DEFAULT);
  const STUDIO_IMAGE = useMarketingImage("home.studio", STUDIO_IMAGE_DEFAULT);
  // Bento grid sources — prefer admin-curated photos, fall back to the
  // static SERVICES covers so the page never looks empty.
  const bento = [0, 1, 2, 3, 4].map((i) => {
    const f = featured[i];
    if (f) return { src: f.thumb_url || f.url, alt: f.caption || "Kiwami" };
    const idx = [0, 3, 2, 4, 1][i];
    return { src: SERVICES[idx].cover, alt: SERVICES[idx].slug };
  });
  // Note: we intentionally do NOT pass `image:` here so the branded OG
  // thumbnail (managed in Admin → Vignette partage) wins on the home route.
  useSEO({
    title:
      lang === "fr"
        ? "Photographie premium — BTP, Événementiel, Sport, Corporate"
        : "Premium photography — Construction, Events, Sports, Corporate",
    description: t("home.hero_sub"),
    lang,
  });

  return (
    <main data-testid="home-page" className="bg-[#050505] text-[#F2EBE5]">
      {/* -------------------- HERO -------------------- */}
      <section
        data-testid="home-hero"
        data-on-dark="true"
        className="relative min-h-screen flex items-end overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Kiwami hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/40 to-[#050505]" />
          <div className="absolute inset-0 bg-[#050505]/25" />
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline kw-accent-line mb-8"
          >
            {t("home.hero_overline")}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display text-[3.5rem] leading-[0.95] md:text-[7rem] lg:text-[9rem] text-[#F2EBE5]"
          >
            {t("home.hero_title_1")}
            <br />
            <em className="text-[#C5A059]">{t("home.hero_title_2")}</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-10 max-w-xl text-base md:text-lg text-[#A19E9C] leading-relaxed"
          >
            {t("home.hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-8"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="kw-btn-primary"
            >
              {t("home.hero_cta_primary")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="kw-btn-ghost"
            >
              {t("home.hero_cta_secondary")}
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute left-6 md:left-12 bottom-8 z-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#A19E9C]"
        >
          <ArrowDown className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={1.25} />
          {t("home.scroll")}
        </motion.div>

        {/* Vertical text */}
        <div className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4 text-[10px] uppercase tracking-[0.4em] text-[#A19E9C]">
          <div className="w-px h-16 bg-[#C5A059]/40" />
          <span className="[writing-mode:vertical-rl] rotate-180">
            {CONTACT.brand} · EST. 2020
          </span>
          <div className="w-px h-16 bg-[#C5A059]/40" />
        </div>
      </section>

      {/* -------------------- ABOUT -------------------- */}
      {isVisible("home_about") && (
      <section
        data-testid="home-about"
        className="relative py-24 md:py-40 max-w-[1400px] mx-auto px-6 md:px-12"
      >
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="md:col-span-5"
          >
            <div className="kw-img-frame aspect-[4/5]">
              <img src={STUDIO_IMAGE} alt="Studio" />
            </div>
          </motion.div>

          <div className="md:col-span-7 md:pl-10">
            <p className="overline kw-accent-line">
              {t("home.about_overline")}
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
            >
              {t("home.about_title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 text-base md:text-lg text-[#A19E9C] leading-relaxed max-w-xl"
            >
              {t("home.about_body")}
            </motion.p>

            <div className="mt-10">
              <Link
                to="/a-propos"
                data-testid="home-about-cta"
                className="kw-btn-ghost"
              >
                {t("home.about_cta")}
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md border-t border-[#F2EBE5]/10 pt-8">
              <div>
                <p className="font-serif-display text-4xl text-[#C5A059]">{c("home.stats.years", "12+")}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A19E9C] mt-2">
                  {lang === "fr" ? "Années" : "Years"}
                </p>
              </div>
              <div>
                <p className="font-serif-display text-4xl text-[#C5A059]">{c("home.stats.projects", "420")}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A19E9C] mt-2">
                  {lang === "fr" ? "Projets" : "Projects"}
                </p>
              </div>
              <div>
                <p className="font-serif-display text-4xl text-[#C5A059]">{c("home.stats.disciplines", "6")}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A19E9C] mt-2">
                  {lang === "fr" ? "Disciplines" : "Disciplines"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* -------------------- SERVICES GRID -------------------- */}
      {isVisible("home_services") && (
      <section
        data-testid="home-services"
        className="relative py-24 md:py-32 max-w-[1400px] mx-auto px-6 md:px-12"
      >
        <SectionTitle
          overline={t("home.services_overline")}
          title={t("home.services_title")}
          sub={t("home.services_sub")}
          testId="home-services-title"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.slug}
              service={s}
              index={i}
              overrideCover={serviceCovers[s.slug]}
            />
          ))}
        </div>
      </section>
      )}

      {/* -------------------- SELECTED WORKS (BENTO) -------------------- */}
      {isVisible("home_works") && (
      <section
        data-testid="home-works"
        className="relative py-24 md:py-32 max-w-[1400px] mx-auto px-6 md:px-12"
      >
        <SectionTitle
          overline={t("home.works_overline")}
          title={t("home.works_title")}
          testId="home-works-title"
        />

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-8 kw-img-frame aspect-[16/10]"
          >
            <img src={bento[0].src} alt={bento[0].alt} loading="lazy" decoding="async" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1 }}
            className="col-span-6 md:col-span-4 kw-img-frame aspect-[4/5]"
          >
            <img src={bento[1].src} alt={bento[1].alt} loading="lazy" decoding="async" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1 }}
            className="col-span-6 md:col-span-4 kw-img-frame aspect-[4/5]"
          >
            <img src={bento[2].src} alt={bento[2].alt} loading="lazy" decoding="async" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="col-span-6 md:col-span-4 kw-img-frame aspect-[4/5]"
          >
            <img src={bento[3].src} alt={bento[3].alt} loading="lazy" decoding="async" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="col-span-12 md:col-span-4 kw-img-frame aspect-[4/5]"
          >
            <img src={bento[4].src} alt={bento[4].alt} loading="lazy" decoding="async" />
          </motion.div>
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/services"
            data-testid="home-works-cta"
            className="kw-btn-ghost"
          >
            {t("home.works_cta")}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
          </Link>
        </div>
      </section>
      )}

      {/* -------------------- CTA BAND -------------------- */}
      {isVisible("home_cta_band") && (
      <section
        data-testid="home-cta-band"
        className="relative py-28 md:py-36 border-y border-[#C5A059]/20 bg-[#0a0a0a]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="overline kw-accent-line">
              {t("home.cta_overline")}
            </p>
            <h2 className="mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.05]">
              {t("home.cta_title")}
            </h2>
            <p className="mt-6 max-w-xl text-[#A19E9C] text-base md:text-lg">
              {t("home.cta_sub")}
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4 items-start md:items-end">
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="kw-btn-primary"
            >
              {t("home.cta_button")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href={CONTACT.phoneHref}
              data-testid="home-cta-phone"
              className="kw-btn-ghost"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={1.25} />
              {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>
      )}
    </main>
  );
};

export default Home;
